import { FC } from 'react';
import Grid from '@mui/material/Grid';
import usImg from '../../media/aboutus.png';
import { Typography, Button, Box, useMediaQuery } from '@mui/material';
import { PlayArrow } from '@mui/icons-material';
import useTranslations from '../global/translation/hooks/translation';
import { useTheme } from '@mui/material/styles';
import { useThemeContext } from '../global/ThemeContext';

const About: FC = () => {
  const { currentLanguage, translations } = useTranslations();
  const text = translations.label.aboutUsTextShort[currentLanguage];
  const theme = useTheme();
  const { darkMode } = useThemeContext();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Grid
      my={6}
      py={isMobile ? 6 : 10}
      container
      justifyContent="center"
      alignItems="flex-start"
      sx={{
        bgcolor: darkMode ? '#121212' : '#FFFFFF',
        maxWidth: '1200px',
        mx: 'auto',
        px: isMobile ? 2 : 4,
      }}
    >
      {/* Title */}
      <Grid item xs={12} mb={4}>
        <Typography
          variant="h4"
          fontWeight={300}
          sx={{
            color: darkMode ? '#FFFFFF' : '#000000',
            textAlign: 'center',
            letterSpacing: '-0.5px',
          }}
        >
          {translations.menuItems.aboutUs[currentLanguage]}
        </Typography>
      </Grid>

      {/* Image + Button Column */}
      <Grid item xs={12} md={5}>
        <Box
          sx={{
            mb: 3,
            position: 'relative',
          }}
        >
          {/* Decorative accent box */}
          <Box
            sx={{
              position: 'absolute',
              top: '20px',
              right: '-20px',
              width: '80%',
              height: '80%',
              border: `2px solid ${darkMode ? '#333333' : '#EEEEEE'}`,
              borderRadius: '50%',
              zIndex: 0,
            }}
          />

          {/* Image container */}
          <Box
            sx={{
              position: 'relative',
              zIndex: 1,
              borderRadius: '50%',
              overflow: 'hidden',
              boxShadow: darkMode ? '0 8px 16px rgba(0, 0, 0, 0.3)' : '0 8px 16px rgba(0, 0, 0, 0.08)',
            }}
          >
            <div style={{ position: 'relative', paddingTop: '100%', margin: '0' }}>
              <img
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                }}
                src={usImg}
                alt="Barbara Urban-Jimenez"
              />
            </div>
          </Box>

          {/* Small accent line */}
          <Box
            sx={{
              position: 'absolute',
              bottom: '-10px',
              left: '20px',
              width: '60px',
              height: '3px',
              bgcolor: theme.palette.primary.main,
              borderRadius: '2px',
              zIndex: 2,
            }}
          />
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Button
            variant="outlined"
            color="primary"
            href="https://dziendobry.tvn.pl/styl-zycia/zwiazki-i-seks/barbara-urban-jimenzes-dzieki-pasji-odnalazla-prawdziwe-uczucie-st5741886"
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<PlayArrow />}
            sx={{
              borderColor: darkMode ? '#FFFFFF' : theme.palette.primary.main,
              color: darkMode ? '#FFFFFF' : theme.palette.primary.main,
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 500,
              px: 3,
              py: 1.5,
              borderRadius: '4px',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                borderColor: darkMode ? '#CCCCCC' : theme.palette.primary.dark,
                backgroundColor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                transform: 'translateY(-2px)',
              },
            }}
          >
            {translations.buttons.watchTvnInterview[currentLanguage]}
          </Button>
        </Box>
      </Grid>

      {/* Text Column */}
      <Grid item xs={12} md={7} sx={{ pl: isMobile ? 0 : 3 }}>
        <Typography
          variant="body1"
          sx={{
            color: darkMode ? '#CCCCCC' : '#555555',
            lineHeight: 1.8,
            fontSize: '16px',
          }}
        >
          {text}
        </Typography>
      </Grid>
    </Grid>
  );
};

export default About;
